import type { PropsWithChildren } from 'react'
import { createContext, useContext, useEffect, useRef, useState } from 'react'
import type {
  AuthenticatedConversationMutationData,
  Conversation,
  ConversationMutationData,
  Device,
  Entitlements,
  MoteStatus,
  MoteStatusPreview
} from '@mote/api'
import { useMoteConversationMutation } from '@mote/api'
import {
  getConvergentMoteStatusProperties,
  pollForConvergentMoteStatusProperties,
  shouldPollForReadyState
} from '@mote/recipes'
import type { PartialConversationResponse } from '../../mote'
import type { PreloadedMoteConversationState } from './PreloadedMoteConversationContext'
import { usePreloadedMoteConversationStateContext } from './PreloadedMoteConversationContext'
import { anonymousConversationMutationData } from './anonymousConversationMutationData'
import {
  isAnonymousMoteDeletedError,
  isAnonymousMoteExpiredError
} from './errorPredicates'

type MotePreviewData = {
  type: 'preview'
  data: PartialConversationResponse
}

type MoteApiData = {
  type: 'api'
  data: PreloadedMoteConversationState
}

type MoteUnknownData = {
  type: 'unknown'
  data: null
}

export type MoteConversationState =
  | MotePreviewData
  | MoteApiData
  | MoteUnknownData

interface MoteStatusLandingPageContextValue {
  getMoteStatus: () => MoteStatus | MoteStatusPreview
  getApiMoteStatus: () => MoteStatus
  getPreviewMoteStatus: () => MoteStatusPreview
  getConversation: () => Conversation
  getEntitlements: () => Entitlements
  getCreateByUserName: (isAnonymous?: boolean) => string
  getCanUseBook: () => boolean
  getHitBookLimit: () => boolean
  getIsWaitingForTranscription: () => boolean
  getDevice: () => Device
  updateMoteStatus: (moteStatus: MoteStatus) => void
  getReplyMoteStatusesCount: () => number
  getIsReply: () => boolean
  getError: () => any
  reload: () => Promise<ConversationMutationData>
  hasData: boolean
  hasPreviewData: boolean
  hasApiData: boolean
  hasConversationData: boolean
  hasError: boolean
  getIsAnonymousMoteExpiredError: () => boolean
  getIsAnonymousMoteDeletedError: () => boolean
  getMyCountedReplies(): string
  getHasReplies(): boolean
  getIsStickyMote(): boolean
  getHasTranscript(): boolean
}

export const MoteConversationContext =
  createContext<MoteStatusLandingPageContextValue | null>(null)

export interface MoteConversationContextProviderProps {
  moteId: string
  motePreview?: PartialConversationResponse | null
}

export function MoteConversationContextProvider(
  props: PropsWithChildren<MoteConversationContextProviderProps>
) {
  const preloadedMoteConversationStateContext =
    usePreloadedMoteConversationStateContext()
  const [state, setState] = useState<MoteConversationState>(() => {
    if (props.motePreview) {
      return {
        type: 'preview',
        data: props.motePreview
      }
    }

    const preloadedState = preloadedMoteConversationStateContext.get()
    if (preloadedState) {
      return {
        type: 'api',
        data: preloadedState
      }
    }

    return {
      type: 'unknown',
      data: null
    }
  })
  const moteConversationMutation = useMoteConversationMutation(
    props.moteId,
    true
  )
  const pollingTimeoutRef = useRef<number | null>(null)
  const shouldAbortRef = useRef<boolean>(false)
  const [isPolling, setIsPolling] = useState<boolean>(false)

  useEffect(() => {
    if (!state.data || getIsMotePreview(state)) {
      load().catch(() => {
        /* do nothing */
      })
    } else if (shouldPollForReadyState(state.data)) {
      poll()
    }
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
      }
    }
  }, [])

  return (
    <MoteConversationContext.Provider
      value={{
        reload: load,
        getMoteStatus,
        getApiMoteStatus,
        getPreviewMoteStatus,
        getConversation,
        getReplyMoteStatusesCount,
        getIsReply,
        getError,
        getDevice,
        getEntitlements,
        getCreateByUserName,
        getCanUseBook,
        getHitBookLimit,
        getIsAnonymousMoteExpiredError,
        getIsAnonymousMoteDeletedError,
        updateMoteStatus,
        getIsWaitingForTranscription,
        getMyCountedReplies: getRepliesAsText,
        getHasReplies,
        getIsStickyMote,
        getHasTranscript,
        hasData: getHasData(state.data),
        hasPreviewData: getHasData(state.data) && getIsMotePreview(state),
        hasApiData: getHasData(state.data) && getIsMoteApi(state),
        hasConversationData: getHasConversationData(state.data),
        hasError: getHasError()
      }}
    >
      {props.children}
    </MoteConversationContext.Provider>
  )

  async function load() {
    if (pollingTimeoutRef.current) {
      shouldAbortRef.current = true
      clearTimeout(pollingTimeoutRef.current)
    }
    const conversationMutationData = await fetchData()
    setState({ type: 'api', data: conversationMutationData })
    if (shouldPollForReadyState(conversationMutationData)) {
      poll()
    }
    return conversationMutationData
  }

  async function fetchData(): Promise<ConversationMutationData> {
    return moteConversationMutation.mutate().then(mutationData => {
      if (mutationData.data) {
        return mutationData.data
      }
      if (mutationData.error) {
        if (
          isAnonymousMoteExpiredError(
            mutationData.error.response,
            mutationData.errorJson
          )
        ) {
          setState({ type: 'api', data: anonymousConversationMutationData })
        }
        throw mutationData.error
      }
      throw new Error("Couldn't load mote.")
    })
  }

  async function poll() {
    try {
      setIsPolling(true)
      const data = await pollForConvergentMoteStatusProperties(
        fetchData,
        true,
        timeoutIdRef => (pollingTimeoutRef.current = timeoutIdRef)
      )
      if (shouldAbortRef.current) {
        shouldAbortRef.current = false
        setIsPolling(false)
        return
      }
      setState(
        getHasData(state.data) && getIsMoteApi(state)
          ? {
              ...state,
              data: {
                ...state.data,
                moteStatus: {
                  ...state.data.moteStatus,
                  ...getConvergentMoteStatusProperties(data.moteStatus)
                }
              }
            }
          : { type: 'api', data }
      )
    } catch {
    } finally {
      setIsPolling(false)
    }
  }

  function getIsMotePreview(
    data: MoteConversationState
  ): data is MotePreviewData {
    return data.type === 'preview'
  }

  function getIsMoteApi(data: MoteConversationState): data is MoteApiData {
    return data.type === 'api'
  }

  function getError() {
    if (!getHasError()) {
      throw new Error('`.error` not defined')
    }
    return moteConversationMutation.error
  }

  function getHasError() {
    return Boolean(moteConversationMutation.error)
  }

  function getData() {
    if (!state.data) {
      throw new Error('`state.data` not defined')
    }
    return state.data
  }

  function getHasData(
    data: PreloadedMoteConversationState | PartialConversationResponse | null
  ): data is PreloadedMoteConversationState | PartialConversationResponse {
    return Boolean(data)
  }

  function getHasConversationData(
    data: PreloadedMoteConversationState | PartialConversationResponse | null
  ): data is ConversationMutationData {
    return getHasData(data) && 'conversation' in data
  }

  function getConversation() {
    if (getHasConversationData(state.data)) {
      return state.data.conversation
    }
    throw new Error('`conversation not defined')
  }

  function getReplyMoteStatusesCount() {
    if (!getHasConversationData(state.data)) {
      return 0
    }

    return getConversation().replyMoteStatuses.length
  }

  function getHasReplies() {
    return getReplyMoteStatusesCount() > 0
  }

  function getRepliesAsText() {
    if (getMoteStatus().wasCreatedBySameUser) {
      const count = getReplyMoteStatusesCount()
      return count === 1 ? '1 Reply' : `${count} Replies`
    }

    return 'Reply'
  }

  function getMoteStatus() {
    return getData().moteStatus
  }

  function getApiMoteStatus() {
    if (getIsMoteApi(state)) {
      return state.data.moteStatus
    }
    throw new Error('`api moteStatus` not defined')
  }

  function getPreviewMoteStatus() {
    if (getIsMotePreview(state)) {
      return state.data.moteStatus
    }
    throw new Error('`preview moteStatus` not defined')
  }

  function getHasTranscript() {
    const transcript = getMoteStatus().transcriptDisplay
    return transcript.trim().length > 0
  }

  function getIsReply() {
    return getMoteStatus().moteId !== getConversation().currentMoteId
  }

  function getDevice() {
    return getData().device
  }

  function isAuthenticatedData(
    data: PreloadedMoteConversationState | PartialConversationResponse
  ): data is AuthenticatedConversationMutationData {
    return data.auth.isLoggedIn
  }

  function getEntitlements() {
    const data = getData()
    if (isAuthenticatedData(data)) {
      return data.entitlements
    }
    throw new Error('Unauthenticated data missing entitlements')
  }

  function getIsWaitingForTranscription() {
    return isPolling
  }

  function updateMoteStatus(moteStatus: MoteStatus) {
    if (!getHasData(state.data)) {
      return
    }

    setState({ type: 'api', data: { ...state.data, moteStatus } })
  }

  function getCreateByUserName() {
    const data = getData()
    return data.moteStatus.isAnonymous
      ? 'Anonymous'
      : data.moteStatus.createdByName
  }

  function getIsAnonymousMoteExpiredError() {
    return (
      getHasError() &&
      getHasData(state.data) &&
      isAnonymousMoteExpiredError(
        moteConversationMutation.error.response,
        moteConversationMutation.errorJson
      )
    )
  }

  function getIsAnonymousMoteDeletedError() {
    return (
      getHasError() &&
      isAnonymousMoteDeletedError(
        moteConversationMutation.error.response,
        moteConversationMutation.errorJson
      )
    )
  }

  function getIsStickyMote() {
    return getMoteStatus().canRedirect && getMoteStatus().canLink
  }

  function getCanUseBook() {
    return fromAuthenticatedDataOrFalse(data => data.entitlements.canUseBook)
  }

  function getHitBookLimit() {
    return fromAuthenticatedDataOrFalse(data => data.entitlements.hitBookLimit)
  }

  function fromAuthenticatedDataOrFalse<
    T extends (data: AuthenticatedConversationMutationData) => boolean
  >(getter: T): boolean {
    const data = getData()
    return isAuthenticatedData(data) ? getter(data) : false
  }
}

export function useMoteConversationContext() {
  const value = useContext(MoteConversationContext)

  if (!value) {
    throw new Error('Missing </MoteConversationContextProvider>')
  }

  return value
}
